import { createSlice } from '@reduxjs/toolkit';
import { 
  createProject, 
  updateProject,
  ProjectState, 
  Layer 
} from "../../shared";

export const INITIAL_STATE: ProjectState = {
  projectId: undefined,
  projectName: 'Untitled Project',
  timestamp: Date.now(),
  layers: [],
  selectedLayers: {},
  dimensions: {
    width: 0,
    height: 0,
  },
  fps: 30,
  durationInFrames: 30,
  stackLength: 1,
  sliceLength: 1,
  type: undefined,
  ownerId: undefined
};

export const project = createSlice({
  name: 'project',
  initialState: INITIAL_STATE,
  reducers: {
    resetProject: (state) => {
      for (const key in INITIAL_STATE) {
        (state as any)[key] = (INITIAL_STATE as any)[key];
      }
    },
    setProjectState: (state, action) => {
      const _state = action.payload;
      for (const key in INITIAL_STATE) {
        if (key in _state) {
          (state as any)[key] = (_state as any)[key];
        } else {
          (state as any)[key] = (INITIAL_STATE as any)[key];
        }
      }
      state.durationInFrames = state.layers.length;
    },
    updateProjectState: (state, action) => {
      const _state = action.payload;
      for (const key in INITIAL_STATE) {
        if (key in _state) {
          (state as any)[key] = (_state as any)[key];
        }
      }
    },
    setDurationInFrames: (state, action) => {
      state.durationInFrames = action.payload;
    },
    setFps: (state, action) => {
      state.fps = action.payload;
    },
    setProjectName: (state, action) => {
      state.projectName = action.payload;
    },
    setStackLength: (state, action) => {
      state.stackLength = action.payload;
    },
    saveProject: (state) => {
      state.timestamp = Date.now();
      if (state.projectId) {
        updateProject(state);
      } else {
        state.projectId = createProject(state) || undefined;
      }
    },
    setLayers: (state, action) => {
      state.layers = action.payload;
      state.durationInFrames = state.layers.length;
    },    
    setDimensions: (state, action) => {
      let { width, height } = action.payload;
      // prevent odd numbers for remotion
      if (width % 2 !== 0) {
        width--;
      }
      if (height % 2 !== 0) {
        height--;
      }
      state.dimensions = { width, height };
    },
    setSelectedLayers: (state, action) => {
      state.selectedLayers = action.payload;
    },
    selectLayer: (state, action) => {
      const index = action.payload;
      console.log("selectLayer", index)
      state.selectedLayers[index] = true;
      state.lastSelectedLayer = index;
    },
    unselectLayer: (state, action) => {
      const index = action.payload;
      state.selectedLayers[index] = false;
    },
    duplicateLayer: (state, action) => {
      const index = action.payload;
      const layer = {...state.layers[index]};
      const copyStr = ' copy';
      const copyStrIndex = layer.name.lastIndexOf(copyStr)
      if (copyStrIndex > -1) {
        let i = 1;
        const val = layer.name.substring(copyStrIndex + copyStr.length);
        if (val) {
          i = parseInt(val) + 1;
        }
        layer.name = layer.name.substring(0, copyStrIndex) + copyStr + ' ' + i;
      } else {
        layer.name += ' copy';
      }
      layer.index = state.layers.length;
      state.layers.push(layer);
      state.durationInFrames = state.layers.length;
    },
    addLayer: (state, action) => {
      const layer: Layer = {...action.payload};
      layer.index = state.layers.length;
      if (!layer.name) {
        // set default layer name
        let maxIndex = 0
        for (let i = 0; i < state.layers.length; i++) {
          const { name } = state.layers[i];
          if (name.startsWith('Layer ')) {
            try {
              const index = parseInt(name.replace('Layer ', ''));
              maxIndex = Math.max(index, maxIndex);
            } catch (e) {
              continue;
            }
          }
        }
        layer.name = 'Layer ' + (maxIndex + 1)
      }
      layer.visible = true;
      state.layers.push(layer);
      state.durationInFrames = state.layers.length;
      state.selectedLayers = {};
      state.selectedLayers[layer.index] = true;
      // state.adjustmentTab = 1; // select properties tab
    },
    moveLayer: (state, action) => {
      const { srcIndex, dstIndex } = action.payload;
      // check if the indices are within the size of the array
      if (srcIndex < state.layers.length && srcIndex >= 0) {
        if (dstIndex < state.layers.length && dstIndex >= 0) {
          // remove the src file
          const srcLayer = state.layers.splice(srcIndex, 1)[0];
          // add the src file to the destination
          state.layers.splice(dstIndex, 0, srcLayer);
          // update indices
          const newLayers: Layer[] = [];
          state.layers.forEach(layer => {
            layer.index = newLayers.length;
            newLayers.push(layer);
          })
          state.layers = newLayers;
        }
      }
    },
    saveLayer: (state, action) => {
      const layer: Layer = action.payload;
      state.layers[layer.index] = layer;
    },
    deleteLayers: (state) => {
      const newLayers: Layer[] = [];
      state.layers.forEach(layer => {
        if (!state.selectedLayers[layer.index]) {
          layer.index = newLayers.length;
          newLayers.push(layer);
        } else {
          delete state.selectedLayers[layer.index];
        }
      })
      state.layers = newLayers;
      state.durationInFrames = state.layers.length;
    },
  },
});

export const {
  resetProject,
  setProjectState,
  updateProjectState,
  saveProject,
  setProjectName,
  setStackLength,
  setLayers,
  setDimensions,
  setSelectedLayers,
  selectLayer,
  unselectLayer,
  duplicateLayer,
  addLayer,
  moveLayer,
  saveLayer,
  deleteLayers,
  setDurationInFrames,
  setFps
} = project.actions;

export default project.reducer;